import { useState } from 'react';
import { SectionShell } from './SectionShell';
import { SectionHeader } from './SectionHeader';
import { ChevronDown } from '../shared/icons/ChevronDown';
import { cn } from '../../lib/utils';
import { CEW_LINK_TARGET, CEW_REPORT_PAGE_URL } from '../../lib/cew-site';

interface AccordionItem {
  readonly id: string;
  readonly question: string;
  readonly body: React.ReactNode;
}

// 2026-07-02 methodology copy refresh (Georgetown / Frank spec PDF).
// Copy across all 4 accordion items rewritten to Georgetown's preferred
// wording. The "Data sources" section is renamed to "Data sources and
// citation" and gains the suggested citation block. Accordion shell +
// styling unchanged.
const ITEMS: ReadonlyArray<AccordionItem> = [
  {
    id: 'overview',
    question: 'Overview',
    body: (
      <>
        <p>
          This tool compares the credentials produced by local middle-skills providers in 55 US
          metro areas to the projected demand for workers in middle-skills springboard occupations
          — the occupations that offer workers above-median earnings at the start of their careers
          and strong wage growth over time.
        </p>
        <p>
          For every metro area and occupational group, the tool shows where credential production
          falls short of &mdash; or exceeds &mdash; projected demand. It also shows how much each
          provider contributes to local alignment, and how many additional credentials would be
          needed to close the gap.
        </p>
      </>
    ),
  },
  {
    id: 'springboard',
    question: 'What counts as a middle-skills springboard occupation?',
    body: (
      <>
        <p>
          In this report, we define middle-skills springboard occupations as those meeting three
          conditions:
        </p>
        <ol className="ml-5 list-decimal space-y-2">
          <li>
            The proportion of workers holding middle-skills credentials (certificates or associate's
            degrees) in the occupation meets or exceeds the proportion of workers holding
            middle-skills credentials in all occupations in that labor market.
          </li>
          <li>
            Starting wages (earnings for workers ages 20&ndash;24) are above the median starting
            wage for all middle-skills occupations in that labor market.
          </li>
          <li>
            Wage growth from the start of the career (ages 20&ndash;24) to mid-career (ages
            35&ndash;44) is above the median for all middle-skills occupations in that labor market.
          </li>
        </ol>
      </>
    ),
  },
  {
    id: 'alignment',
    question: 'How is credentials-to-jobs alignment measured?',
    body: (
      <>
        <p>
          We measure alignment using a credentials-to-jobs ratio, which represents each metro
          area's annual number of credentials produced that align with springboard occupations
          within each occupational group divided by the projected annual number of job openings
          available in all occupations (both springboard occupations and other occupations) that
          align with those credentials.
        </p>
        <ul className="ml-5 list-disc space-y-1">
          <li>
            <strong>Ratio below 1.0</strong> &mdash; credential shortage (fewer credentials than
            openings)
          </li>
          <li>
            <strong>Ratio above 1.0</strong> &mdash; credential surplus (more credentials than
            openings)
          </li>
        </ul>
        <p>
          The ratio is built on the program-of-study-to-occupation (CIP-SOC) crosswalk developed
          by the US Bureau of Labor Statistics and the US Department of Education.{' '}
          <a
            href="https://cew.georgetown.edu/wp-content/uploads/sb_cip_soc_list.xlsx"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-link underline hover:no-underline"
          >
            Download the springboard CIP-SOC crosswalk (XLSX)
          </a>
        </p>
      </>
    ),
  },
  {
    id: 'sources',
    question: 'Data sources and citation',
    body: (
      <>
        <p>
          The findings in this data tool reflect Georgetown University Center on Education and the
          Workforce analysis of the following data sources:
        </p>
        <ul className="ml-5 list-disc space-y-2">
          <li>
            US Census Bureau. American Community Survey (ACS): 2012&ndash;19 and 2021&ndash;23.
            Ruggles, Steven, Sarah Flood, Matthew Sobek, Danika Brockman, Grace Cooper, Stephanie
            Richards, and Megan Schouweiler. IPUMS USA: Version 13.0 [dataset]. IPUMS, 2023.{' '}
            <a
              href="https://doi.org/10.18128/D010.V13.0"
              className="text-blue-link underline hover:no-underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://doi.org/10.18128/D010.V13.0
            </a>
            .
          </li>
          <li>
            US Department of Education, National Center for Education Statistics (NCES). Integrated
            Postsecondary Education Data System (IPEDS). 2021&ndash;23.
          </li>
          <li>
            US Department of Labor, Bureau of Labor Statistics (BLS). Employment Projections:
            Employment by Detailed Occupation. 2025.
          </li>
          <li>
            2020 Classification of Instructional Programs to Standard Occupational Classification
            (CIP-SOC) Crosswalk from the Bureau of Labor Statistics and the US Department of
            Education's National Center for Education Statistics.
          </li>
        </ul>
        <p>Our suggested citation for this data tool is:</p>
        <ul className="ml-5 list-disc space-y-2">
          {/* CR-037 (Frank email 2026-08-31): final published citation —
            "[Data tool]. Designed by Free Range." and the "[url]" placeholder
            replaced by the report page URL (see CEW_REPORT_PAGE_URL for the
            launch-day liveness caveat), rendered as a live link that
            navigates the parent frame (DEC-029 iframe → CEW_LINK_TARGET). */}
          <li>
            McLeod, Emma Nyhof, Catherine Morris, and Jeff Strohl.{' '}
            <em>
              Springboards to Opportunity: The Middle-Skills Occupational Landscape in 55 US Metro
              Areas
            </em>
            . Georgetown University Center on Education and the Workforce, 2026.{' '}
            <a
              href={CEW_REPORT_PAGE_URL}
              className="text-blue-link underline hover:no-underline"
              target={CEW_LINK_TARGET}
              rel="noopener"
            >
              cew.georgetown.edu/springboards-to-opportunity
            </a>
            .
          </li>
        </ul>
      </>
    ),
  },
];

// ChevronDown is now imported from `shared/icons/ChevronDown` and used
// inline at the trigger site below. Local definition removed per the
// quality-cleanup PR — the canonical chevron lives in
// `shared/icons/ChevronDown` and is used across BrandSelect /
// OccupationGroupCombobox / MetroAreaCombobox.

/** "04 · Methodology" — five-item accordion. First item open by default. */
export function MethodologySection() {
  const [openId, setOpenId] = useState<string>(ITEMS[0]?.id ?? '');

  return (
    <SectionShell id="methodology" variant="alt">
      <SectionHeader
        eyebrow="04 · Methodology"
        headline="How we built the data"
        lead="Read on to understand our definition of a middle-skills springboard occupation, the full scope of our work, and our methodology. Tap any section below to learn more."
      />

      {/* Accordion spans the full section content width so its left/right
          edges line up with the card sections above (How It Works / Data
          Views / Who It's For), which render directly against the section
          edge. The prototype's 24px `.accordion-wrap` side inset (line 515)
          is intentionally dropped — on this page it pushed the accordion
          ~24px inboard of those cards. */}
      <div>
        <div className="overflow-hidden rounded-lg border border-border bg-white">
          {ITEMS.map((item) => {
            const isOpen = item.id === openId;
            return (
              <div key={item.id} className="border-b border-border-light last:border-b-0">
                <h3>
                  <button
                    type="button"
                    aria-expanded={isOpen}
                    aria-controls={`acc-panel-${item.id}`}
                    id={`acc-btn-${item.id}`}
                    onClick={() => setOpenId(isOpen ? '' : item.id)}
                    // Trigger bg per `_archive/home.html` lines 527-528:
                    // hover AND open both paint with --light-blue (#EAF4F9
                    // = our `ice` token). Open state is persistent until
                    // the user toggles a different item.
                    className={`group flex w-full items-center justify-between gap-4 px-10 py-5 text-left text-card-title font-semibold text-text-1 transition hover:bg-ice ${
                      isOpen ? 'bg-ice' : ''
                    }`}
                  >
                    <span>{item.question}</span>
                    <ChevronDown
                      aria-hidden="true"
                      className={cn(
                        'h-4 w-4 transition-transform',
                        isOpen ? 'rotate-180 text-navy' : 'text-text-4',
                      )}
                    />
                  </button>
                </h3>
                {/* Smooth open/close transition using the
                    `grid-template-rows: 0fr → 1fr` trick — no JS
                    height measurement, no layout thrash, content
                    always in DOM (so screen readers can find it
                    even when collapsed; aria-hidden + the grid
                    collapse hide it visually + from AT). 200ms
                    ease-out per the polish-system base duration;
                    motion-reduce drops the transition entirely. */}
                <div
                  // A11y (WCAG 4.1.2, axe aria-hidden-focus): the panel stays in
                  // the DOM when collapsed (so the 0fr→1fr transition works), so
                  // its links would otherwise remain in the tab order while
                  // visually hidden — a keyboard user tabbing into an invisible
                  // link. `inert` removes the collapsed subtree from focus AND the
                  // a11y tree. Set as the DOM *property* via ref because React
                  // 18.3 mis-serializes the boolean attribute (`inert="false"`
                  // still reads as inert). Redundant aria-hidden kept as defense.
                  ref={(el) => {
                    if (el) el.inert = !isOpen;
                  }}
                  className={`grid transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none ${
                    isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                  }`}
                  aria-hidden={!isOpen}
                >
                  <div className="overflow-hidden">
                    <div
                      id={`acc-panel-${item.id}`}
                      role="region"
                      aria-labelledby={`acc-btn-${item.id}`}
                      // Panel padding mirrors prototype line 553
                      // `.acc-panel { padding: 24px 40px 24px; }` — 24px
                      // top + bottom, 40px sides. Missing top padding
                      // was a day-one bug in the React port (commit
                      // 6c559fd) — never introduced by any refactor,
                      // just never carried over from the prototype.
                      // Restored as `py-6` here. Paragraphs additionally
                      // get `pt-1` (4px) to mirror `.acc-panel p
                      // { padding-top: 4px }`, so the first paragraph
                      // effectively sits 28px below the trigger and
                      // consecutive paragraphs read with 16px gap.
                      // Paragraphs use leading 1.8 (panel default);
                      // list items override to 1.75 to match prototype
                      // line 537.
                      // CR-022: accordion body copy raised text-sm (14px) →
                      // text-base (16px) — homepage paragraph/list text must
                      // clear the 16px floor like every other body-copy site.
                      className="space-y-3 px-10 py-6 text-base leading-[1.8] text-text-3 [&_p]:pt-1 [&_li]:leading-[1.75]"
                    >
                      {item.body}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </SectionShell>
  );
}


